import * as React from 'react';
import { graphql } from 'react-apollo';

import { useQuery } from '@apollo/client';
import { branch, compose, withProps, withState } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import User from 'js/lib/user';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import { PROGRAM_VISIBILITY_RULE } from 'bundles/enterprise-legacy-learner-home/constants/ProgramCommonConstants';
import {
  C4C_CORONA_TRIAL,
  C4C_FREEMIUM,
  C4C_UPSWELL,
  C4WR_CORONA_TRIAL,
  COURSERA_CORE,
  GWG_FREEMIUM,
} from 'bundles/enterprise-legacy-learner-home/constants/ProgramTypes';
import type {
  EnterpriseProgramBySlugQueryResult,
  EnterpriseProgramBySlugQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/EnterpriseProgramBySlugQuery';
import EnterpriseProgramBySlugQuery from 'bundles/enterprise-legacy-learner-home/queries/EnterpriseProgramBySlugQuery';
import FindUserEmailsByUserIdQuery from 'bundles/enterprise-legacy-learner-home/queries/FindUserEmailsByUserIdQuery';
import type {
  FindUserEmailsByUserIdQueryResult,
  FindUserEmailsByUserIdQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/FindUserEmailsByUserIdQuery';
import type { EnterpriseProgramsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseProgramsV1';
import type { ProgramBrowsingExperiencesV1 as ProgramBrowsingExperiencesV1Type } from 'bundles/naptimejs/resources/__generated__/ProgramBrowsingExperiencesV1';
import type { ThirdPartyOrganizationsV1 as ThirdPartyOrganizationsV1Type } from 'bundles/naptimejs/resources/__generated__/ThirdPartyOrganizationsV1';
import EnterpriseNoticeAcceptanceLogsV1 from 'bundles/naptimejs/resources/enterpriseNoticeAcceptanceLogs.v1';
import ProgramBrowsingExperienceV1 from 'bundles/naptimejs/resources/programBrowsingExperiences.v1';
import ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import type {
  EnterpriseNoticeAcceptanceQuery,
  EnterpriseNoticeAcceptanceQueryVariables,
} from 'bundles/program-home/queries/EnterpriseNoticeAcceptanceQuery';
import EnterpriseNoticeAcceptanceQueryText from 'bundles/program-home/queries/EnterpriseNoticeAcceptanceQuery';
import ProgramMembershipsQuery from 'bundles/program-home/queries/ProgramMembershipsQuery.graphql';
import type {
  ProgramMembershipsQueryQuery,
  ProgramMembershipsQueryQueryVariables,
} from 'bundles/program-home/queries/__generated__/ProgramMembershipsQuery';
import {
  type ProgramMembershipType,
  newProgramMembershipInstance,
} from 'bundles/program-home/types/ProgramMembershipAdapter';
import EnterpriseProgramContext from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import ThirdPartyOrganizationContext from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import type { PropsFromWithEnterpriseAdminRoles } from 'bundles/unified-common/hocs/withEnterpriseAdminRoles';
import withEnterpriseAdminRoles from 'bundles/unified-common/hocs/withEnterpriseAdminRoles';
import { useRecoveryEmail } from 'bundles/unified-common/hooks/useRecoveryEmail';
import EnterpriseUserRostersByProgramId from 'bundles/unified-common/queries/EnterpriseUserRostersByProgramId.graphql';
import type {
  EnterpriseUserRostersByProgramIdQuery,
  EnterpriseUserRostersByProgramIdQueryVariables,
  GetEnterpriseContractFragment,
  GetEnterpriseContractTagFragment,
} from 'bundles/unified-common/queries/__generated__/EnterpriseUserRostersByProgramId';

const WILDCARD_BROWSING_EXPERIENCE = 'DEEP_BROWSE';

type PropsFromWithProps = {
  userId: number;
  isAuthenticatedUser: boolean;
  isSuperuser: boolean;
};

type PropsFromWithEnterpriseProgramDataQuery = {
  programs?: EnterpriseProgramsV1[] | null;
  thirdPartyOrg?: ThirdPartyOrganizationsV1Type | null;
  programBrowsingExperience?: ProgramBrowsingExperiencesV1Type | null;
  programContractTags?: GetEnterpriseContractTagFragment[] | null;
  enterpriseContracts?: GetEnterpriseContractFragment[] | null;
  renderErrorPage: boolean;
};

type PropsFromCAWithProps = {
  isCareerAcademyV2: boolean;
};

type PropsFromWithEnterpriseProgramDataWithProps = {
  program: EnterpriseProgramsV1;
  programId: string;
  programName: string;
  thirdPartyOrganizationId: string;
  requirePreJoinCheck: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  orgName: string;
  isC4cv: boolean;
  isUpswell: boolean;
  isC4er: boolean;
  isC4wr: boolean;
  isC4C: boolean;
  isGwGProgram: boolean;
  isWESContract: boolean;
  showNoCertificateNotification: boolean;
  isC4Program: boolean;
};

type PropsFromWithEnterpriseProgramData = PropsFromWithEnterpriseProgramDataQuery &
  PropsFromWithEnterpriseProgramDataWithProps &
  PropsFromCAWithProps;

type PropsForProgramHomeMembershipsQuery = Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'programId'>;

type PropsForEnterpriseNoticeAcceptanceQuery = Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'thirdPartyOrganizationId' | 'requirePreJoinCheck'> &
  Pick<PropsFromWithEnterpriseProgramData, 'requirePreJoinCheck'>;

type PropsFromProgramHomeMembershipsQuery = {
  renderErrorPage: boolean;
  programMembership: ProgramMembershipType;
  reloadProgramMembership?: () => void;
  isProgramMember: boolean;
  isInvited: boolean;
  membershipCreatedAt?: number | null;
  isMembershipLoading: boolean;
};

type PropsFromEnterpriseNoticeAcceptanceLogs = {
  acceptanceNoticeLogs?: EnterpriseNoticeAcceptanceLogsV1[];
};

type PropsFromProgramHomeLoggedInQuery = PropsFromProgramHomeMembershipsQuery & PropsFromEnterpriseNoticeAcceptanceLogs;

type PropsForDeferredProgramHomeLoggedInQuery = Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'programId'>;

type PropsFromDeferredProgramHomeLoggedInQuery = {
  renderErrorPage: boolean;
  isProgramAdmin: boolean;
};

type PropsForWithLoggedInData = PropsFromProgramHomeMembershipsQuery &
  PropsFromEnterpriseNoticeAcceptanceLogs &
  PropsForDeferredProgramHomeLoggedInQuery;

type PropsFromWithLoggedInData = Required<Omit<PropsFromProgramHomeLoggedInQuery, 'renderErrorPage'>> &
  Required<Omit<PropsFromDeferredProgramHomeLoggedInQuery, 'renderErrorPage'>>;

type PropsFromCaller = {
  children: React.ReactNode;
};

type PropsFromRouter = {
  programSlug: string;
};

type PropsFromProgram = {
  program?: EnterpriseProgramsV1;
  thirdPartyOrganization?: ThirdPartyOrganizationsV1Type;
  isProgramLoading?: boolean;
  requirePreJoinCheck?: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
  isWildcard?: boolean;
};

type PropsFromFindUserEmailsByUserIdQuery = {
  userEmails?: UserEmail[];
  refetchUserEmails?: () => Promise<unknown> | unknown;
};

type PropsToComponent = PropsFromCaller &
  PropsFromProgram &
  PropsFromWithEnterpriseAdminRoles &
  PropsFromFindUserEmailsByUserIdQuery &
  PropsFromWithLoggedInData & {
    programBrowsingExperience?: ProgramBrowsingExperiencesV1Type;
    programContractTags?: GetEnterpriseContractTagFragment[];
    enterpriseContracts?: GetEnterpriseContractFragment[];
  };

type PropsForWithLoggedInDataBranch = {
  userId?: string;
  program?: EnterpriseProgramsV1;
  isProgramLoading?: boolean;
};

const EnterpriseDataProvider = ({
  children,
  program,
  isProgramLoading,
  thirdPartyOrganization,
  programMembership,
  isProgramMember,
  membershipCreatedAt,
  isMembershipLoading,
  isProgramAdmin,
  isInvited,
  userEmails,
  refetchUserEmails,
  isWildcard,
  acceptanceNoticeLogs,
  requirePreJoinCheck,
  browsingExperienceType,
  programBrowsingExperience,
}: PropsToComponent) => {
  const { data } = useQuery<EnterpriseUserRostersByProgramIdQuery, EnterpriseUserRostersByProgramIdQueryVariables>(
    EnterpriseUserRostersByProgramId,
    {
      variables: { programId: program?.id || '' },
      context: { clientName: 'gatewayGql' },
      skip: !program?.id || isProgramLoading || !User.isAuthenticatedUser(),
    }
  );

  const enterpriseContracts =
    data?.EnterpriseRosters?.queryRostersByProgramIdsOrContractIdsOrBothAndIncludeDeleted.elements.map(
      (roster) => roster.contract
    );

  const programContractTags = enterpriseContracts?.map((contract) => {
    return {
      contractTagName: contract.contractTag?.contractTagName,
      addedAt: contract.contractTag?.addedAt,
      addedBy: contract.contractTag?.addedBy,
      contractId: contract.contractTag?.contractId,
      source: contract.contractTag?.source,
    } as GetEnterpriseContractTagFragment;
  });
  const contractTagNames = programContractTags?.map((tag) => tag.contractTagName);

  const hasFreemiumTag = contractTagNames?.some((tag) => tag === C4C_FREEMIUM);
  const isC4cv = contractTagNames?.some((tag) => tag === C4C_CORONA_TRIAL) || hasFreemiumTag;
  const isUpswell = contractTagNames?.some((tag) => tag === C4C_UPSWELL);
  const isC4C = contractTagNames?.some((tag) => tag && tag.startsWith('C4C'));
  const isC4wr = contractTagNames?.some((tag) => tag === C4WR_CORONA_TRIAL);
  const isGwGProgram = contractTagNames?.some((tag) => tag && GWG_FREEMIUM.includes(tag));
  const showNoCertificateNotification = isUpswell || isC4cv || isC4wr || hasFreemiumTag;
  const isC4er = contractTagNames?.some((tag) => tag === COURSERA_CORE);
  const isC4Program = isC4cv || isC4er || isC4wr || isUpswell;

  const isWESContract = enterpriseContracts?.[0]?.contractType === 'SELF_SERVE';

  const { recoveryEmail, isRecoveryEmailLoading } = useRecoveryEmail(
    User.get().id as number,
    thirdPartyOrganization?.id
  );

  return (
    <EnterpriseProgramContext.Provider
      value={{
        program,
        isProgramLoading,
        isC4cv,
        isC4C,
        isUpswell,
        programMembership,
        isProgramMember,
        isProgramAdmin,
        isInvited,
        userEmails,
        hasFreemiumTag,
        isGwGProgram,
        showNoCertificateNotification,
        membershipCreatedAt,
        isMembershipLoading,
        isWildcard,
        refetchUserEmails,
        acceptanceNoticeLogs,
        requirePreJoinCheck,
        browsingExperienceType,
        programBrowsingExperience,
        programContractTags,
        enterpriseContracts,
        isC4er,
        isC4wr,
        isWESContract,
        isC4Program,
        recoveryEmail,
        isRecoveryEmailLoading,
      }}
    >
      <ThirdPartyOrganizationContext.Provider value={thirdPartyOrganization}>
        {children}
      </ThirdPartyOrganizationContext.Provider>
    </EnterpriseProgramContext.Provider>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(
  connectToRouter((router) => {
    return {
      programSlug: router.params.programSlug || router.location.query?.programSlug,
    };
  }),
  withState('showFreemiumCourseReenrollModal', 'setShowFreemiumCourseReenrollModal', true),
  graphql<PropsFromRouter, EnterpriseProgramBySlugQueryResult, EnterpriseProgramBySlugQueryVariables, PropsFromProgram>(
    EnterpriseProgramBySlugQuery,
    {
      options: ({ programSlug }) => ({ variables: { programSlug } }),
      props: ({ data }) => {
        const programs = data?.EnterpriseProgramsV1?.slug?.elements;
        const programBrowsingExperience = data?.EnterpriseProgramsV1?.slug?.linked?.programBrowsingExperiencesV1?.[0];
        const thirdPartyOrganization = data?.EnterpriseProgramsV1?.slug?.linked?.thirdPartyOrganizationsV1?.[0];

        let thirdPartyOrg = null;
        if (thirdPartyOrganization) {
          thirdPartyOrg = new ThirdPartyOrganizationsV1(thirdPartyOrganization);
        }
        const requirePreJoinCheck = thirdPartyOrg
          ? thirdPartyOrg.programVisibilityRule === PROGRAM_VISIBILITY_RULE.ACCEPTED_PRIVACY_NOTICE
          : false;

        const browsingExperienceType = programBrowsingExperience
          ? new ProgramBrowsingExperienceV1(programBrowsingExperience)
          : undefined;

        const isWildcard =
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          data?.EnterpriseProgramsV1?.slug?.linked?.programBrowsingExperiencesV1?.[0].browsingExperience ===
          WILDCARD_BROWSING_EXPERIENCE;

        return {
          programBrowsingExperience,
          requirePreJoinCheck,
          browsingExperienceType,
          program: programs ? programs[0] : undefined,
          thirdPartyOrganization,
          thirdPartyOrganizationId: thirdPartyOrganization?.id,
          isProgramLoading: data?.loading,
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          programId: programs ? programs[0].id : undefined,
          isWildcard,
          failedToFetchProgram: !!data?.error,
        };
      },
    }
  ),
  withProps<PropsFromWithProps, {}>(() => ({
    userId: User.get().id,
    isAuthenticatedUser: User.isAuthenticatedUser(),
    isSuperuser: User.isSuperuser(),
  })),
  branch<PropsForWithLoggedInDataBranch>(
    (props) => Boolean(props.userId) && !props.program?.metadata.endedAt && !props.isProgramLoading,
    compose<PropsForWithLoggedInData, PropsFromWithLoggedInData>(
      graphql<
        PropsForProgramHomeMembershipsQuery,
        ProgramMembershipsQueryQuery,
        ProgramMembershipsQueryQueryVariables,
        PropsFromProgramHomeMembershipsQuery
      >(ProgramMembershipsQuery, {
        options: ({ programId, userId }) => ({
          variables: {
            programMembershipIds: [{ userId, programId }],
          },
          context: { clientName: 'gatewayGql' },
          skip: !programId || !userId,
        }),
        props: ({ data }): PropsFromProgramHomeMembershipsQuery => {
          const programMembershipData = data?.ProgramMemberships?.queryProgramMemberships?.edges?.[0]?.node;
          const programMembership = newProgramMembershipInstance(programMembershipData);
          const isInvited = Boolean(programMembership.isInvited);
          return {
            renderErrorPage: Boolean(data?.error),
            programMembership,
            reloadProgramMembership: data?.refetch,
            isProgramMember: Boolean(programMembership.isProgramMember),
            isInvited,
            membershipCreatedAt: programMembershipData?.createdAt
              ? new Date(programMembershipData.createdAt).getTime()
              : undefined,
            isMembershipLoading: Boolean(data?.loading),
          };
        },
      }),
      graphql<
        PropsForEnterpriseNoticeAcceptanceQuery,
        EnterpriseNoticeAcceptanceQuery,
        EnterpriseNoticeAcceptanceQueryVariables,
        PropsFromEnterpriseNoticeAcceptanceLogs
      >(EnterpriseNoticeAcceptanceQueryText, {
        options: ({ userId, thirdPartyOrganizationId, requirePreJoinCheck }) => ({
          variables: {
            userId: userId.toString(),
            thirdPartyOrganizationId,
            requirePreJoinCheck,
          },
        }),
        props: ({ data, ownProps: { requirePreJoinCheck } }): PropsFromEnterpriseNoticeAcceptanceLogs => {
          const enterpriseNoticeAcceptanceLogsV1 = requirePreJoinCheck
            ? data?.EnterpriseNoticeAcceptanceLogsV1?.getByUserIdAndOrganizationId?.elements
            : undefined;
          const acceptanceNoticeLogs = enterpriseNoticeAcceptanceLogsV1?.map(
            (x) => new EnterpriseNoticeAcceptanceLogsV1(x)
          );
          return {
            acceptanceNoticeLogs,
          };
        },
      }),
      graphql<
        {},
        FindUserEmailsByUserIdQueryResult,
        FindUserEmailsByUserIdQueryVariables,
        PropsFromFindUserEmailsByUserIdQuery
      >(FindUserEmailsByUserIdQuery, {
        props: ({ data }) => {
          const userEmails = data?.FindUserEmailsByUserIdResponse?.userEmails;
          const refetchUserEmails = data?.refetch;
          return { userEmails, refetchUserEmails };
        },
        options: () => ({
          variables: {
            userId: `${User.get().id}`,
          },
          errorPolicy: 'all',
          ssr: false,
        }),
      }),
      withEnterpriseAdminRoles
    )
  )
)(EnterpriseDataProvider);
